import { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { Home, Share2, Activity as ActivityIcon, Wallet as WalletIcon, Search, Copy, LogOut, Send, Camera, User, Link2, CheckSquare, Square, FilePlus, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, money, imgSrc } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Logo, Loading } from "@/components/shared";
import SnapBusiness from "@/pages/SnapBusiness";
import AddBusiness from "@/pages/AddBusiness";
import { RequestsList, AskRequest, RequestDetail } from "@/pages/Requests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OwnerEditButton, YourBusinessPill } from "@/components/OwnerEdit";
import { FeedbackCard, FeedbackLoginPrompt, TrustBadge } from "@/components/ReferralFeedback";
import { useBrowseLocation } from "@/hooks/useBrowseLocation";
import SetAreaBanner from "@/components/SetAreaBanner";
import BrowseServiceChips from "@/components/BrowseServiceChips";
import { browseLocationLine } from "@/lib/browseLocation";
import { SHARE_PREF_KEY, audienceForShareChoice } from "@/lib/referralShare";
import { whatsappShareMessage } from "@/lib/growthHooks";
import { parseVcard } from "@/lib/vcard";

const STATUS_STYLE = {
  paid: "bg-brand-green/15 text-brand-green",
  converted: "bg-brand-gold/20 text-brand-gold-dark",
  stale: "bg-secondary text-muted-foreground",
  clicked: "bg-blue-500/15 text-blue-600",
  shared: "bg-secondary text-muted-foreground",
};

const AppNav = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();
  const tabs = [
    { to: "/app", icon: Home, label: "Home" },
    { to: "/app/requests", icon: MessageCircleQuestion, label: "Requests" },
    { to: "/app/activity", icon: ActivityIcon, label: "Activity" },
    { to: "/app/wallet", icon: WalletIcon, label: "Wallet" },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
        <Logo />
        <nav className="flex min-w-0 flex-wrap items-center justify-end gap-0.5 sm:gap-1">
          {tabs.map((t) => {
            const active = t.to === "/app" ? loc.pathname === "/app" : loc.pathname.startsWith(t.to);
            return (
              <Link key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase()}`}
                aria-label={t.label}
                className={`flex items-center gap-1.5 rounded-[10px] px-2 py-2 text-xs font-medium sm:gap-2 sm:px-3 sm:text-sm ${active ? "bg-brand-green text-white" : "text-muted-foreground hover:text-foreground"}`}>
                <t.icon className="h-4 w-4 shrink-0" /><span>{t.label}</span>
              </Link>
            );
          })}
          <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} data-testid="app-logout" aria-label="Log out"><LogOut className="h-4 w-4" /></Button>
        </nav>
      </div>
    </header>
  );
};

const HomeTab = () => {
  const nav = useNavigate();
  const vcardRef = useRef(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [trust, setTrust] = useState(null);
  const {
    searchParams,
    needsAreaFallback,
    areaInput,
    setAreaInput,
    areaError,
    savingArea,
    setArea,
    coords,
  } = useBrowseLocation({ enabled: true });
  const load = async (query = "") => {
    const { data } = await api.get("/branches/search", { params: { q: query, ...searchParams } });
    setResults(data);
    setSearched(!!query.trim());
  };
  const loadFeedback = () => {
    api.get("/me/feedback/pending").then(({ data }) => setPendingFeedback(data || [])).catch(() => {});
    api.get("/me/trust").then(({ data }) => setTrust(data)).catch(() => {});
  };
  useEffect(() => { load(); loadFeedback(); }, [coords?.lat, coords?.lng]);

  const onVcardFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const card = parseVcard(text);
      if (!card.name && !card.phone) {
        toast.error("Couldn't read that card. Try another .vcf file.");
        return;
      }
      const { data } = await api.post("/me/contacts/import-vcard", card);
      if (data.matched_branch?.id) {
        nav(`/b/${data.matched_branch.id}`);
        return;
      }
      const d = data.draft || card;
      const qs = new URLSearchParams();
      if (d.name) qs.set("name", d.name);
      if (d.phone) qs.set("phone", d.phone);
      if (d.address) qs.set("address", d.address);
      nav(`/app/add-business?${qs.toString()}`);
    } catch (err) {
      toast.error(apiErr(err) || "Couldn't import that card.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-black tracking-tight">Find a business to refer</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => nav("/app/snap")} data-testid="add-from-photo-btn">
            <Camera className="mr-2 h-4 w-4" /> Add from photo
          </Button>
          <Button variant="outline" disabled={importing} onClick={() => vcardRef.current?.click()} data-testid="import-vcard-button">
            <FilePlus className="mr-2 h-4 w-4" /> {importing ? "Importing…" : "Import a business card (.vcf)"}
          </Button>
          <input ref={vcardRef} type="file" accept=".vcf,text/vcard,text/x-vcard" className="hidden" onChange={onVcardFile} data-testid="import-vcard-input" />
        </div>
      </div>
      <SetAreaBanner
        visible={needsAreaFallback}
        areaInput={areaInput}
        setAreaInput={setAreaInput}
        onSubmit={() => setArea().then((ok) => ok && load(q))}
        error={areaError}
        saving={savingArea}
      />
      {trust?.badge || trust?.tier ? (
        <div className="mt-4 rounded-[12px] border border-border bg-card px-4 py-3" data-testid="trust-profile-summary">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Your referrer standing</p>
            <TrustBadge badge={trust.badge || trust.tier} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {trust.points} point{trust.points === 1 ? "" : "s"}
            {trust.next_tier ? ` · ${trust.next_tier.points_needed} to ${trust.next_tier.label}` : " · top tier"}
          </p>
          {trust.history?.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground" data-testid="trust-history">
              {trust.history.slice(0, 5).map((h, i) => (
                <li key={i}>
                  Feedback {h.effect}
                  {h.points_delta ? ` (${h.points_delta > 0 ? "+" : ""}${h.points_delta})` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {pendingFeedback.length > 0 ? (
        <div className="mt-4 space-y-3" data-testid="pending-feedback-list">
          {pendingFeedback.map((item) => (
            <FeedbackCard key={item.id} item={item} onAnswered={() => loadFeedback()} />
          ))}
        </div>
      ) : null}
      <div className="mt-4 flex items-center gap-2 rounded-[12px] border border-border bg-card p-2">
        <Search className="ml-2 h-5 w-5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
          placeholder="Search businesses…" data-testid="app-search-input" className="flex-1 bg-transparent px-2 py-2 outline-none" />
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => load(q)} data-testid="app-search-btn">Search</Button>
      </div>
      {searched && results.length === 0 ? (
        <div className="mt-8 rounded-[14px] border border-dashed border-border bg-card p-6 text-center" data-testid="app-search-empty">
          <p className="text-muted-foreground">No businesses found.</p>
          <Button
            className="mt-4 bg-brand-green hover:bg-brand-green-dark"
            onClick={() => nav(`/app/add-business?name=${encodeURIComponent(q.trim())}`)}
            data-testid="add-missing-business-btn"
          >
            Can&apos;t find them? Add {q.trim()} to ReferLinc
          </Button>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => nav(`/app/requests/new?q=${encodeURIComponent(q.trim() ? `Who knows a good ${q.trim()}?` : "")}`)}
            data-testid="ask-community-from-search-btn"
          >
            No one found — ask the community
          </Button>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {results.map((b) => {
            const photo = b.portfolio_image_urls?.[0];
            return (
            <div key={b.id} className="overflow-hidden rounded-[14px] border border-border bg-card" data-testid={`app-branch-${b.id}`}>
              <div className="relative h-36 w-full bg-gradient-to-br from-brand-green/20 to-brand-gold/20">
                {photo ? (
                  <img src={imgSrc(photo)} alt={b.company_name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-4xl font-black text-muted-foreground/40">
                    {b.company_name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                {b.subcategory_badge && (
                  <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm" data-testid={`sub-badge-${b.id}`}>
                    {b.subcategory_badge}
                  </span>
                )}
                <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2.5 py-0.5 text-[13px] font-bold tracking-wide text-brand-gold backdrop-blur-sm" data-testid="payer-tier-badge" title="Payer tier — relative generosity">
                  {b.payer_tier || "$"}
                </span>
                {b.is_owner && (
                  <div className="absolute bottom-2 right-2">
                    <OwnerEditButton branch={b} />
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-lg font-semibold">{b.company_name}</h3>
                  <YourBusinessPill branch={b} className="shrink-0" />
                </div>
                {browseLocationLine(b) ? (
                  <p className="text-sm text-muted-foreground" data-testid={`browse-loc-${b.id}`}>{browseLocationLine(b)}</p>
                ) : null}
                <BrowseServiceChips branch={b} />
                {b.reward_active && b.reward?.reward_type === "cash" && (
                  <p className="mt-2 font-bold text-brand-gold-dark dark:text-brand-gold">Earn {money(b.reward.reward_amount_cents)} per referral</p>
                )}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => nav(`/b/${b.id}`)} data-testid={`view-${b.id}`}>View</Button>
                  {b.reward_active && (
                    <Button size="sm" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark" onClick={() => nav(`/app/refer/${b.id}`)} data-testid={`refer-${b.id}`}>
                      <Share2 className="mr-1 h-4 w-4" /> Refer
                    </Button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReferShareTab = () => {
  const { branchId } = useParams();
  const [branch, setBranch] = useState(null);
  const [mode, setMode] = useState(null); // 'person' | 'link' | null
  const [contactsOnly, setContactsOnly] = useState(() => {
    try { return localStorage.getItem(SHARE_PREF_KEY) === "1"; } catch { return false; }
  });
  const [recipient, setRecipient] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remindBusy, setRemindBusy] = useState(false);

  useEffect(() => {
    api.get(`/branches/${branchId}`).then(({ data }) => setBranch(data));
    setMode(null);
    setResult(null);
    setRecipient("");
  }, [branchId]);

  const persistContactsOnly = (next) => {
    setContactsOnly(next);
    try { localStorage.setItem(SHARE_PREF_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };

  const createLink = async ({ audience, channel, recipient_phone = null }) => {
    setLoading(true);
    try {
      const { data } = await api.post("/referrals", {
        branch_id: branchId,
        audience,
        channel,
        recipient_phone,
      });
      setResult(data);
      if (data.warning) toast.warning(data.warning);
      else toast.success("Referral link created!");
      return data;
    } catch (e) {
      toast.error(apiErr(e));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const shareLinkAnywhere = async (channel = "copy_link") => {
    setMode("link");
    await createLink({
      audience: audienceForShareChoice("link", contactsOnly),
      channel,
    });
  };

  const sendToPerson = async () => {
    if (!recipient.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setMode("person");
    await createLink({
      audience: audienceForShareChoice("person", false),
      channel: "direct_contact",
      recipient_phone: recipient.trim(),
    });
  };

  const remind = async () => {
    if (!branch || remindBusy || branch.reward_reminder_sent) return;
    setRemindBusy(true);
    try {
      await api.post(`/branches/${branch.id}/reward-reminder`);
      setBranch({ ...branch, reward_reminder_sent: true });
      toast.success("Reminder sent");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setRemindBusy(false);
    }
  };

  if (!branch) return <Loading />;
  const fullUrl = result ? `${window.location.origin}/r/${result.code}` : "";
  const showPause =
    !branch.reward_active && branch.verification_status === "verified" && !branch.is_owner;
  const reminderSent = !!branch.reward_reminder_sent;
  const askBoost = branch.ask_boost;
  const firstEarn = askBoost?.first_conversion_cents;
  const standardEarn = branch.reward?.reward_amount_cents;
  const subtitle = showPause
    ? (reminderSent
      ? "Reward paused — reminder sent"
      : "Reward paused — new referrals won’t earn right now")
    : askBoost && firstEarn
      ? `First conversion: ${money(firstEarn)} (includes ${money(askBoost.bonus_cents)} bonus), then ${money(standardEarn)}`
      : branch.reward_active && branch.reward?.reward_type === "cash"
        ? `Earn ${money(standardEarn)} per converted referral`
        : "Share a business you trust";

  return (
    <div className="mx-auto max-w-lg px-4 py-6" data-testid="referral-share-page">
      <h1 className="text-2xl font-black tracking-tight" data-testid="referral-sheet-title">
        Refer {branch.company_name}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="referral-sheet-subtitle">{subtitle}</p>
      {showPause && !reminderSent ? (
        <button
          type="button"
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green"
          onClick={remind}
          disabled={remindBusy}
          data-testid="referral-sheet-remind"
        >
          {remindBusy ? "Sending…" : "Remind business"}
        </button>
      ) : null}

      {branch.reward_active && branch.reward?.reward_type === "cash" && !showPause && (
        <div className="mt-4 rounded-[14px] bg-gradient-to-br from-brand-green via-brand-green-dark to-[#0e3f30] p-5 text-white" data-testid="referral-earn-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">
            {askBoost ? "First conversion" : "You earn"}
          </p>
          <p className="text-3xl font-black">{money(askBoost ? firstEarn : standardEarn)}</p>
          <p className="text-sm text-white/80">
            {askBoost
              ? `Includes ${money(askBoost.bonus_cents)} one-time bonus · then ${money(standardEarn)}`
              : "per converted referral"}
          </p>
        </div>
      )}

      {!result ? (
        <div className="mt-6 space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">How do you want to refer?</p>

          <button
            type="button"
            data-testid="share-mode-person"
            onClick={() => setMode("person")}
            className={`flex w-full items-start gap-3 rounded-[14px] border p-4 text-left transition ${
              mode === "person" ? "border-brand-green bg-brand-green/5" : "border-border bg-card"
            }`}
          >
            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mode === "person" ? "bg-brand-green text-white" : "bg-secondary text-brand-green"}`}>
              <User className="h-4 w-4" />
            </span>
            <span className="flex-1">
              <span className="block font-semibold">Send to a specific person</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">Pick a contact — only they can use it</span>
            </span>
          </button>

          {mode === "person" ? (
            <div className="space-y-3 rounded-[14px] border border-border bg-card p-4" data-testid="share-person-panel">
              <Input
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="+1 555 987 6543"
                data-testid="recipient-phone-input"
              />
              <p className="text-xs text-muted-foreground">Opens the claim window immediately for this person.</p>
              <Button
                className="w-full bg-brand-green hover:bg-brand-green-dark"
                disabled={loading}
                onClick={sendToPerson}
                data-testid="create-referral-person-btn"
              >
                {loading ? "Creating…" : "Create link for this person"}
              </Button>
            </div>
          ) : null}

          <button
            type="button"
            data-testid="share-mode-link"
            onClick={() => setMode("link")}
            className={`flex w-full items-start gap-3 rounded-[14px] border p-4 text-left transition ${
              mode === "link" ? "border-brand-green bg-brand-green/5" : "border-border bg-card"
            }`}
          >
            <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mode === "link" ? "bg-brand-green text-white" : "bg-secondary text-brand-green"}`}>
              <Link2 className="h-4 w-4" />
            </span>
            <span className="flex-1">
              <span className="block font-semibold">Share the link anywhere</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                {contactsOnly
                  ? "Only people in your contacts can use it"
                  : "WhatsApp, text, groups — anyone with it can use it"}
              </span>
            </span>
          </button>

          {mode === "link" ? (
            <div className="space-y-3" data-testid="share-delivery-row">
              <Button
                className="w-full bg-brand-green hover:bg-brand-green-dark"
                disabled={loading}
                onClick={() => shareLinkAnywhere("copy_link")}
                data-testid="create-referral-btn"
              >
                {loading ? "Creating…" : "Create shareable link"}
              </Button>
            </div>
          ) : null}

          <button
            type="button"
            data-testid="share-advanced-contacts"
            onClick={() => persistContactsOnly(!contactsOnly)}
            className="mt-2 flex w-full items-start gap-2 border-t border-border pt-4 text-left"
          >
            {contactsOnly ? (
              <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
            ) : (
              <Square className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span>
              <span className="block text-sm font-medium text-muted-foreground">Limit to my contacts</span>
              {contactsOnly ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Shared links only work for people in your contacts.
                </span>
              ) : null}
            </span>
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {result.warning && (
            <div className="rounded-[10px] bg-brand-gold/15 px-4 py-3 text-sm text-brand-gold-dark" data-testid="referral-warning">{result.warning}</div>
          )}
          <div className="rounded-[12px] border border-border bg-card p-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Your link</p>
            <p className="mt-1 break-all font-mono text-sm" data-testid="referral-link-value">{fullUrl}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success("Copied!"); }} data-testid="copy-link-btn">
              <Copy className="mr-2 h-4 w-4" /> Copy link
            </Button>
            <a href={`https://wa.me/?text=${encodeURIComponent(whatsappShareMessage(branch.company_name, fullUrl))}`} target="_blank" rel="noreferrer" data-testid="whatsapp-share-btn">
              <Button className="bg-brand-green hover:bg-brand-green-dark"><Send className="mr-2 h-4 w-4" /> Share on WhatsApp</Button>
            </a>
          </div>
          <Link to="/app/activity" className="block text-sm text-brand-green underline" data-testid="goto-activity">View in Activity →</Link>
        </div>
      )}
    </div>
  );
};

const ActivityTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/referrals/mine").then(({ data }) => setItems(data)); }, []);
  if (items === null) return <Loading />;
  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <h1 className="text-2xl font-black tracking-tight">Your referrals</h1>
      {items.length === 0 ? (
        <p className="mt-4 text-muted-foreground">No referrals yet. Find a business on Home to get started.</p>
      ) : (
        <div className="mt-4 flex flex-col gap-2">
          {items.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3" data-testid={`activity-${r.id}`}>
              <div className="flex-1">
                <p className="font-semibold">{r.company_name}</p>
                <p className="text-xs text-muted-foreground">{r.clicks} clicks · {r.audience.replace("_", " ")}</p>
              </div>
              {r.amount_cents ? <span className="font-bold text-brand-green">{money(r.amount_cents)}</span> : null}
              <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLE[r.status] || "bg-secondary"}`} data-testid={`status-${r.id}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WalletTab = () => {
  const { refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const load = () => api.get("/wallet").then(({ data }) => setWallet(data));
  useEffect(() => { load(); }, []);

  const cashOut = async () => {
    try {
      await api.post("/wallet/cash-out");
      toast.success("Cash-out initiated!");
      load(); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!wallet) return <Loading />;
  const canCash = wallet.available_balance_cents >= wallet.cash_out_minimum_cents;
  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <div className="rounded-[16px] bg-gradient-to-br from-brand-green via-brand-green-dark to-[#0e3f30] p-6 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">Available balance</p>
        <p className="mt-1 text-5xl font-black" data-testid="wallet-balance">{money(wallet.available_balance_cents)}</p>
        <Button className="mt-4 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark" disabled={!canCash} onClick={cashOut} data-testid="cash-out-btn">
          Cash out
        </Button>
        {!canCash && <p className="mt-2 text-sm text-white/70">Minimum cash-out is {money(wallet.cash_out_minimum_cents)}.</p>}
      </div>
      {!wallet.tax_info_collected && (
        <div className="mt-4 rounded-[12px] border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="tax-note">
          Tax info will be requested automatically as your yearly payouts approach the reporting threshold.
        </div>
      )}
      <h2 className="mt-8 text-lg font-bold">Transactions</h2>
      <div className="mt-3 flex flex-col gap-2">
        {wallet.transactions.length === 0 ? (
          <p className="text-muted-foreground">No transactions yet.</p>
        ) : wallet.transactions.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-[10px] border border-border bg-card px-4 py-2 text-sm">
            <span className="capitalize">{t.type.replace(/_/g, " ")}</span>
            <span className={t.amount_cents < 0 ? "text-destructive" : "text-brand-green"}>{money(t.amount_cents)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ReferrerApp() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [prompt, setPrompt] = useState(null);
  useEffect(() => { if (ready && !user) nav("/login?redirect=/app"); }, [ready, user]);
  useEffect(() => {
    if (!ready || !user) return;
    api.get("/me/feedback/prompt").then(({ data }) => {
      if (data?.prompt) setPrompt(data.prompt);
    }).catch(() => {});
  }, [ready, user]);
  if (!ready || !user) return <Loading />;
  return (
    <div className="min-h-screen">
      <AppNav />
      {prompt ? <FeedbackLoginPrompt prompt={prompt} onDone={() => setPrompt(null)} /> : null}
      <Routes>
        <Route index element={<HomeTab />} />
        <Route path="snap" element={<SnapBusiness />} />
        <Route path="add-business" element={<AddBusiness />} />
        <Route path="requests" element={<RequestsList />} />
        <Route path="requests/new" element={<AskRequest />} />
        <Route path="requests/:id" element={<RequestDetail />} />
        <Route path="refer/:branchId" element={<ReferShareTab />} />
        <Route path="activity" element={<ActivityTab />} />
        <Route path="wallet" element={<WalletTab />} />
      </Routes>
    </div>
  );
}
